/* Billing Terms → Terms check list: every client with billing lines in the month or flagged PTL, with the terms in
   force on the 1st (or missing), and the bulk upload of terms (template pre-filled with the clients missing terms;
   preview, then accept). Upload needs PTL_BILLING. */
import React, { useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { apiConstants, getDownload } from '../../../common/constants'
import { BILLING_BASE, useTermsBulkMutation, useTermsCheckQuery } from '../../../redux/ptlBillingApiSlice'
import { canBill, downloadFile, errText, fmtDate, monthLabel, recentMonths } from '../billingUtils'

export default function TermsCheckList({ month, onMonth, onOpenClient }) {
  const { data = [], isFetching, error } = useTermsCheckQuery(month)
  const [onlyMissing, setOnlyMissing] = useState(true)
  const [bulkOpen, setBulkOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const billing = canBill()
  const rows = useMemo(() => (onlyMissing ? data.filter((r) => r.missing) : data), [data, onlyMissing])
  const missing = data.filter((r) => r.missing).length
  const missingWithShipments = data.filter((r) => r.missing && r.shipments > 0).length

  const template = () => downloadFile(getDownload, `${apiConstants.BASE_LOCAL}${BILLING_BASE}/terms/bulk/template?month=${month}`, 'PTL_Billing_Terms_Template.xlsx')
    .catch(() => enqueueSnackbar('Could not download the template.', { variant: 'error' }))

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField select size='small' label='Service month' value={month} onChange={(e) => onMonth(e.target.value)} sx={{ minWidth: 180 }}>
          {recentMonths(12).map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />} label='Only clients missing terms' />
        <Box sx={{ flex: 1 }} />
        <Button variant='outlined' onClick={template} sx={{ textTransform: 'none' }}>Template (pre-filled with missing)</Button>
        <Button variant='contained' color='warning' disabled={!billing} onClick={() => setBulkOpen(true)} sx={{ textTransform: 'none' }}>Bulk upload terms</Button>
      </Box>
      {missingWithShipments > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          <b>{missingWithShipments} clients with shipments in {monthLabel(month)} have no terms</b>: their lines are blocked as "terms missing" until terms are added
          (choose "not in contract" when DPH does not apply; leave a surcharge's columns empty when it is not in the contract).
        </Alert>
      )}
      {error && <Alert severity='error'>{errText(error)}</Alert>}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>{data.length} clients, {missing} with no terms on any day of {monthLabel(month)} {isFetching && '(loading…)'}</Typography>
      <Table size='small'>
        <TableHead>
          <TableRow><TableCell>Client</TableCell><TableCell>PTL flag</TableCell><TableCell align='right'>Shipments this month</TableCell><TableCell align='right'>Blocked for terms</TableCell><TableCell>Terms in force</TableCell><TableCell /></TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.merchantOid}>
              <TableCell sx={{ fontWeight: 500 }}>{r.merchantName || r.merchantOid}</TableCell>
              <TableCell>{r.ptlFlag ? <Chip size='small' label='PTL' color='success' variant='outlined' /> : <Chip size='small' label='NOT FLAGGED' color='warning' variant='outlined' />}</TableCell>
              <TableCell align='right'>{r.shipments}</TableCell>
              <TableCell align='right' sx={{ color: r.linesBlockedForTerms ? 'error.main' : undefined }}>{r.linesBlockedForTerms}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {r.missing ? <Chip size='small' color='error' label='MISSING' />
                  : <>{r.startsOn && <Chip size='small' color='warning' variant='outlined' label={`FROM ${fmtDate(r.startsOn).toUpperCase()}`} sx={{ mr: 1, whiteSpace: 'nowrap' }} />}{r.termsInForce}</>}
              </TableCell>
              <TableCell align='right'><Button size='small' onClick={() => onOpenClient(r.merchantOid)} sx={{ textTransform: 'none' }}>Open ›</Button></TableCell>
            </TableRow>
          ))}
          {!isFetching && rows.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>{onlyMissing ? 'Every client has terms.' : 'No clients.'}</TableCell></TableRow>}
        </TableBody>
      </Table>
      {bulkOpen && <TermsBulkDialog onClose={() => setBulkOpen(false)} onTemplate={template} />}
    </Box>
  )
}

function TermsBulkDialog({ onClose, onTemplate }) {
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)
  const [err, setErr] = useState(null)
  const [bulk, { isLoading }] = useTermsBulkMutation()
  const { enqueueSnackbar } = useSnackbar()
  const send = async (confirm) => {
    setErr(null)
    const res = await bulk({ file, confirm })
    if (res.error) return setErr(errText(res.error))
    setResult(res.data)
    if (confirm) enqueueSnackbar(`${res.data.savedRows} terms versions added, ${res.data.skippedRows} skipped.`, { variant: 'success' })
  }
  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Bulk upload: billing terms<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Check what you are uploading before it is added.</Typography></DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button variant='outlined' component='label' size='small' sx={{ textTransform: 'none' }}>
            {file ? file.name : 'Choose filled template (.xlsx)'}
            <input hidden type='file' accept='.xlsx' onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null) }} />
          </Button>
          <Button size='small' onClick={onTemplate} sx={{ textTransform: 'none' }}>Download template</Button>
        </Box>
        {err && <Alert severity='error'>{err}</Alert>}
        {result && (
          <>
            <Alert severity={result.accepted ? 'success' : 'info'}>
              {result.accepted ? `${result.savedRows} added, ${result.skippedRows} skipped.` : `${result.okRows} rows will be added, ${result.skippedRows} skipped.`}
            </Alert>
            <Table size='small'>
              <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Client</TableCell><TableCell>Effective from</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
              <TableBody>
                {result.rows.map((r) => (
                  <TableRow key={r.row} sx={r.ok ? undefined : { bgcolor: '#FFF6F5' }}>
                    <TableCell>{r.row}</TableCell><TableCell>{r.merchantName || r.key}</TableCell><TableCell>{r.chargeType}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}><Chip size='small' color={r.ok ? 'success' : 'error'} label={r.ok ? (result.accepted ? 'ADDED' : 'OK') : 'SKIP'} sx={{ mr: 1 }} />{r.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result?.accepted ? 'Close' : 'Cancel'}</Button>
        {!result?.accepted && <LoadingButton variant='outlined' loading={isLoading} disabled={!file} onClick={() => send(false)}>Preview</LoadingButton>}
        {result && !result.accepted && <LoadingButton variant='contained' color='success' loading={isLoading} disabled={!result.okRows} onClick={() => send(true)}>Yes, I accept ({result.okRows} rows)</LoadingButton>}
      </DialogActions>
    </Dialog>
  )
}
